import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { slideUp } from '@/lib/motion';
import { useAISettings, useGenerateCourse, useAIJob } from '../../services/aiCourses';
import { uploadFile } from '../../services/uploads';
import {
  type AIDurationOption,
  type AIImagesOption,
  type AILayoutOption,
} from '../../types/aiCourse';

const STEP_LABELS: Record<string, string> = {
  research: 'Researching the topic',
  outline: 'Designing the outline',
  content: 'Writing content',
  quizzes: 'Creating quizzes',
  media: 'Generating images & diagrams',
  finalize: 'Finishing up',
};

const DURATION_OPTIONS: { value: AIDurationOption; label: string }[] = [
  { value: 'auto', label: 'Let AI decide from the content' },
  { value: 'quick', label: 'Quick — around 30 minutes' },
  { value: 'standard', label: 'Standard — 1 to 2 hours' },
  { value: 'deep', label: 'In-depth — 3 hours or more' },
];

const IMAGES_OPTIONS: { value: AIImagesOption; label: string }[] = [
  { value: 'none', label: 'No AI images' },
  { value: 'headers', label: 'Header images (course, sections, content)' },
  { value: 'content', label: 'Visuals inside the content' },
  { value: 'both', label: 'Headers + visuals in the content' },
];

const LAYOUT_OPTIONS: { value: AILayoutOption; label: string }[] = [
  { value: 'cards', label: 'Visual cards — a header picture per section' },
  { value: 'list', label: 'Simple list — a clean table of contents' },
];

const ACCEPTED_DOCS = '.pdf,.txt,.md,.docx';
const MAX_SOURCE_FILES = 5;

interface SourceDoc {
  name: string;
  key: string;
}

const AICourseGeneratePage: React.FC = () => {
  const navigate = useNavigate();
  const { data: settings, isLoading: settingsLoading } = useAISettings();

  const [topic, setTopic] = useState('');
  const [urlsText, setUrlsText] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);

  // Source documents: uploaded immediately on pick under one batch id (no
  // course exists yet), sent along as file_keys on generate.
  const batchIdRef = useRef<string>(crypto.randomUUID().replace(/-/g, ''));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sourceDocs, setSourceDocs] = useState<SourceDoc[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);

  // Generation options (product spec 2026-07-14)
  // Quizzes are what most trainers expect a generated course to contain, so
  // the choice starts on — but it IS a choice, which it was not before
  // (Aleix, 2026-09-15).
  const [includeQuizzes, setIncludeQuizzes] = useState(true);
  // The topic field is a one-liner that reads like a title; instructions need
  // room of their own.
  const [preferences, setPreferences] = useState('');
  const [includeSurvey, setIncludeSurvey] = useState(false);
  const [includeCertificate, setIncludeCertificate] = useState(false);
  const [images, setImages] = useState<AIImagesOption>('none');
  const [duration, setDuration] = useState<AIDurationOption>('auto');
  const [aiLayout, setAiLayout] = useState<AILayoutOption>('cards');

  const generate = useGenerateCourse();
  const { data: job } = useAIJob(jobId);

  const handleFilesPicked = async (files: FileList | null) => {
    if (!files?.length) return;
    const room = MAX_SOURCE_FILES - sourceDocs.length;
    const picked = Array.from(files).slice(0, room);
    if (files.length > room) {
      toast.error(`Up to ${MAX_SOURCE_FILES} documents per course.`);
    }
    setUploadingCount((c) => c + picked.length);
    await Promise.all(
      picked.map(async (file) => {
        try {
          const res = await uploadFile({
            file,
            resource_type: 'ai_source',
            resource_id: batchIdRef.current,
          });
          setSourceDocs((docs) => [...docs, { name: file.name, key: res.key }]);
        } catch {
          toast.error(`Couldn't upload ${file.name}`);
        } finally {
          setUploadingCount((c) => c - 1);
        }
      })
    );
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const aiReady = !!settings?.available && !!settings?.enabled;

  useEffect(() => {
    if (job?.status === 'succeeded' && job.course_id) {
      toast.success('Your AI course draft is ready!');
      // Land in the EDITOR (review/preview/save as draft) — not Configure,
      // which pushes toward assigning learners before anyone reviewed the
      // AI's work (product decision 2026-07-14).
      navigate(`/courses/${job.course_id}/edit`);
    }
  }, [job, navigate]);

  const stepLabel = useMemo(() => {
    if (!job) return 'Starting…';
    return (job.current_step && STEP_LABELS[job.current_step]) || 'Working…';
  }, [job]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanUrls = urlsText
      .split('\n')
      .map((u) => u.trim())
      .filter(Boolean);
    try {
      const res = await generate.mutateAsync({
        topic: topic.trim(),
        urls: cleanUrls,
        file_keys: sourceDocs.map((d) => d.key),
        include_quizzes: includeQuizzes,
        preferences: preferences.trim(),
        include_survey: includeSurvey,
        include_certificate: includeCertificate,
        images,
        duration,
        layout: aiLayout,
      });
      setJobId(res.job_id);
    } catch {
      // error toast surfaced by the mutation hook
    }
  };

  const resetForRetry = () => setJobId(null);

  // ── AI disabled / loading gates ─────────────────────────────────────────
  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-text-secondary">
        <span className="spinner mr-3" /> Loading…
      </div>
    );
  }

  if (!aiReady) {
    return (
      <motion.div className="max-w-2xl mx-auto py-12 text-center" {...slideUp}>
        <h1 className="text-2xl font-semibold text-text-primary mb-3">AI Course Creation Is Off</h1>
        <p className="text-text-secondary">
          AI features aren’t enabled for this workspace yet. An owner or admin can
          turn them on in <span className="font-medium">Settings → AI</span>.
        </p>
      </motion.div>
    );
  }

  // ── Generating / terminal states ────────────────────────────────────────
  if (jobId) {
    const failed = job?.status === 'failed';
    return (
      <motion.div className="max-w-2xl mx-auto py-12" {...slideUp}>
        <h1 className="text-2xl font-semibold text-text-primary mb-6">
          {failed ? 'Generation failed' : 'Building your course…'}
        </h1>

        {!failed && (
          <>
            <div className="flex items-center justify-between mb-2 text-sm text-text-secondary">
              <span>{stepLabel}</span>
              <span>{job?.progress_percent ?? 0}%</span>
            </div>
            <div className="w-full h-2 rounded-full bg-background-secondary overflow-hidden">
              <div
                className="h-full bg-primary-600 dark:bg-primary-350 transition-all duration-500"
                style={{ width: `${job?.progress_percent ?? 0}%` }}
              />
            </div>
            <p className="mt-6 text-sm text-text-secondary">
              You can leave this page — we’ll notify you when the draft is ready.
            </p>
          </>
        )}

        {failed && (
          <div>
            <p className="text-text-secondary mb-6">
              {job?.error_message || 'Something went wrong while generating the course.'}
              {' '}You can retry, or open the partial draft to edit it manually.
            </p>
            <div className="flex gap-3">
              <button
                onClick={resetForRetry}
                className="px-6 py-2 rounded-full bg-primary-600 dark:bg-primary-350 hover:bg-primary-700 dark:hover:bg-primary-400 transition-colors"
              >
                Try again
              </button>
              {job?.course_id && (
                <button
                  onClick={() => navigate(`/courses/${job.course_id}/edit`)}
                  className="px-6 py-2 rounded-full border border-primary-100 dark:border-primary-800 hover:border-primary-200 dark:hover:border-primary-700 text-primary-600 dark:text-primary-350 transition-colors"
                >
                  Open draft
                </button>
              )}
            </div>
          </div>
        )}
      </motion.div>
    );
  }

  // ── Form ────────────────────────────────────────────────────────────────
  return (
    <motion.div className="max-w-2xl mx-auto py-10" {...slideUp}>
      {/* Picking Teri was a one-way door — changing your mind meant starting
          the whole "Create course" flow over (Aleix, 2026-09-15). */}
      <button
        type="button"
        onClick={() => navigate('/courses/create')}
        className="inline-flex items-center gap-1.5 mb-6 px-3.5 py-1.5 rounded-full text-xs font-bold text-text-primary border border-border-primary/40 hover:bg-text-primary/[0.06] active:scale-[0.97] focus-ring transition-[background-color,transform] duration-150"
      >
        ← Back to course creation
      </button>
      <h1 className="text-3xl font-semibold text-text-primary mb-2">Create a Course with AI</h1>
      <p className="text-text-secondary mb-8">
        Give a topic and any reference links. We’ll research it and build a draft
        course — sections, content, quizzes, and visuals — for you to refine.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label htmlFor="topic" className="text-sm font-medium text-text-primary">
            Topic <span className="text-error-500">*</span>
          </label>
          <input
            id="topic"
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. Introduction to Python for data analysis"
            minLength={3}
            maxLength={200}
            required
            className="px-4 py-3 rounded-xl border border-border-primary bg-background-primary text-text-primary focus:border-primary-500 focus:outline-none"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="preferences" className="text-sm font-medium text-text-primary">
            Anything else we should know?{' '}
            <span className="text-text-secondary font-normal">(optional)</span>
          </label>
          <textarea
            id="preferences"
            value={preferences}
            onChange={(e) => setPreferences(e.target.value)}
            rows={4}
            maxLength={2000}
            data-testid="ai-preferences"
            placeholder={
              'Who it is for, how long, the tone, what to emphasise, what to leave out…\n'
              + 'e.g. "For new hires with no technical background. Keep it practical, '
              + 'use our own onboarding vocabulary, and avoid anything about pricing."'
            }
            className="px-4 py-3 rounded-xl border border-border-primary bg-background-primary text-text-primary focus:border-primary-500 focus:outline-none resize-y"
          />
          <p className="text-xs text-text-secondary">
            Written instructions for the course author — the more specific, the
            closer the draft lands.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="urls" className="text-sm font-medium text-text-primary">
            Reference URLs <span className="text-text-secondary font-normal">(optional, one per line)</span>
          </label>
          <textarea
            id="urls"
            value={urlsText}
            onChange={(e) => setUrlsText(e.target.value)}
            rows={4}
            placeholder={'https://example.com/guide\nhttps://docs.example.com'}
            className="px-4 py-3 rounded-xl border border-border-primary bg-background-primary text-text-primary focus:border-primary-500 focus:outline-none resize-y"
          />
        </div>

        {/* ── Source documents ── */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-text-primary">
            Source documents{' '}
            <span className="text-text-secondary font-normal">
              (optional — PDF, Word, text; up to {MAX_SOURCE_FILES})
            </span>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_DOCS}
            multiple
            className="hidden"
            data-testid="ai-source-input"
            onChange={(e) => handleFilesPicked(e.target.files)}
          />
          <div className="flex flex-wrap items-center gap-2">
            {sourceDocs.map((doc) => (
              <span
                key={doc.key}
                className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full bg-primary-500/10 text-sm text-text-primary"
                data-testid="ai-source-chip"
              >
                <DocumentTextIcon className="w-4 h-4 text-primary-500" aria-hidden="true" />
                <span className="max-w-[220px] truncate">{doc.name}</span>
                <button
                  type="button"
                  aria-label={`Remove ${doc.name}`}
                  onClick={() =>
                    setSourceDocs((docs) => docs.filter((d) => d.key !== doc.key))
                  }
                  className="p-0.5 rounded-full text-text-tertiary hover:text-text-primary hover:bg-text-tertiary/10 transition-colors"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </span>
            ))}
            {sourceDocs.length < MAX_SOURCE_FILES && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingCount > 0}
                className="px-4 py-2 rounded-full border border-dashed border-border-primary text-sm text-text-secondary hover:text-text-primary hover:border-primary-500/60 transition-colors disabled:opacity-60"
                data-testid="ai-source-add"
              >
                {uploadingCount > 0 ? `Uploading ${uploadingCount}…` : '+ Add documents'}
              </button>
            )}
          </div>
        </div>

        {/* ── Duration ── */}
        <div className="flex flex-col gap-2">
          <label htmlFor="duration" className="text-sm font-medium text-text-primary">
            Course duration
          </label>
          <select
            id="duration"
            value={duration}
            onChange={(e) => setDuration(e.target.value as AIDurationOption)}
            className="px-4 py-3 rounded-xl border border-border-primary bg-background-primary text-text-primary focus:border-primary-500 focus:outline-none"
          >
            {DURATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* ── AI imagery ── */}
        <div className="flex flex-col gap-2">
          <label htmlFor="images" className="text-sm font-medium text-text-primary">
            AI-generated images
          </label>
          <select
            id="images"
            value={images}
            onChange={(e) => setImages(e.target.value as AIImagesOption)}
            className="px-4 py-3 rounded-xl border border-border-primary bg-background-primary text-text-primary focus:border-primary-500 focus:outline-none"
          >
            {IMAGES_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* ── Section layout ── */}
        <div className="flex flex-col gap-2">
          <label htmlFor="ai-layout" className="text-sm font-medium text-text-primary">
            Section layout
          </label>
          <select
            id="ai-layout"
            value={aiLayout}
            onChange={(e) => setAiLayout(e.target.value as AILayoutOption)}
            className="px-4 py-3 rounded-xl border border-border-primary bg-background-primary text-text-primary focus:border-primary-500 focus:outline-none"
          >
            {LAYOUT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* ── Extras ── */}
        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-2.5 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={includeQuizzes}
              onChange={(e) => setIncludeQuizzes(e.target.checked)}
              className="w-4 h-4 rounded border-border-primary"
              data-testid="ai-include-quizzes"
            />
            Include knowledge-check quizzes
          </label>
          <label className="flex items-center gap-2.5 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={includeSurvey}
              onChange={(e) => setIncludeSurvey(e.target.checked)}
              className="w-4 h-4 rounded border-border-primary"
              data-testid="ai-include-survey"
            />
            Include a feedback survey at the end of the course
          </label>
          <label className="flex items-center gap-2.5 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={includeCertificate}
              onChange={(e) => setIncludeCertificate(e.target.checked)}
              className="w-4 h-4 rounded border-border-primary"
              data-testid="ai-include-certificate"
            />
            Offer a completion certificate
          </label>
        </div>

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={generate.isPending || uploadingCount > 0 || topic.trim().length < 3}
            className="px-7 py-2.5 rounded-full bg-primary-600 dark:bg-primary-350 hover:bg-primary-700 dark:hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {generate.isPending ? 'Starting…' : 'Generate course'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/courses/create')}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default AICourseGeneratePage;
